import { useCallback, useEffect, useRef, useState } from 'react';
import { useAiChatStore } from '../../../ai-chat/store/aiChatStore';
import { useClaudeCliDetection } from '../../../ai-chat/hooks/useClaudeCliDetection';

/**
 * AI Chat sidebar state for a query editor tab: the persisted per-tab open
 * flag, the toggle/close handlers (gated behind Claude CLI detection), the
 * "CLI required" dialog flag, and the parent-width observer that bounds the
 * sidebar's max width. The chat SESSION itself stays in the host component —
 * it depends on connection info resolved later in the render. Behavior carried
 * verbatim from the previous inline implementation.
 */
export function useAiChatSidebar(tabId: string) {
  const isAiChatOpen = useAiChatStore(useCallback((s) => s.getTabState(tabId).sidebarOpen, [tabId]));
  const toggleAiChatSidebar = useAiChatStore((s) => s.toggleSidebar);
  const setAiChatSidebarOpen = useAiChatStore((s) => s.setSidebarOpen);
  const [showCliRequiredDialog, setShowCliRequiredDialog] = useState(false);
  const { checkAndOpenSidebar } = useClaudeCliDetection();

  const handleToggleAiChat = useCallback(() => {
    checkAndOpenSidebar(
      () => toggleAiChatSidebar(tabId),
      () => setShowCliRequiredDialog(true)
    );
  }, [checkAndOpenSidebar, toggleAiChatSidebar, tabId]);

  const handleCloseAiChat = useCallback(() => {
    setAiChatSidebarOpen(tabId, false);
  }, [setAiChatSidebarOpen, tabId]);

  // Track parent container width for sidebar max width
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = useState(800);

  useEffect(() => {
    const el = outerContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setParentWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return {
    isAiChatOpen,
    showCliRequiredDialog,
    setShowCliRequiredDialog,
    handleToggleAiChat,
    handleCloseAiChat,
    outerContainerRef,
    parentWidth,
  };
}
